/**
 * Created by neo on 23.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { OnlineMeetingEntry } from '../../../Model/Explore/OnlineMeetingEntry';
import { ExploreContentViewHostCard } from '../ExploreContentViewHostCard';
import { ExploreContentViewOnlineMeetingContent } from './ExploreContentViewOnlineMeetingContent';
import { ExploreContentViewOnlineMeetingReminder } from './ExploreContentViewOnlineMeetingReminder';
import { useEffect } from 'react';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { ShareButton } from '../../ShareButton';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { ExploreContentViewOnlineMeetingMediaContainer } from './ExploreContentViewOnlineMeetingMediaContainer';
import { Theme } from '../../../Theme/Theme';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
`;

const MediaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 32px 0;

  @media (max-width: 768px) {
    padding: 16px 16px 0;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 32px 64px;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 24px 16px 48px;
  }
`;

const ContentCard = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
  background: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 24px;
    gap: 24px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 280px;
  max-width: 300px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ReminderCard = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 20px;
  padding: 24px;
`;

export type ExploreContentViewOnlineMeetingProps = {
  entry: OnlineMeetingEntry;
};

export const ExploreContentViewOnlineMeeting: React.FC<ExploreContentViewOnlineMeetingProps> = observer(({ entry }) => {
  const { analytics } = useRootStore();

  const { canJoinMeeting, nextDate } = entry;
  const title = useLocalized(entry.name);
  const description = useLocalized(entry.description);

  useContentMetaTags({ title, description });

  const eventLogged = React.useRef(false);

  useEffect(() => {
    if (canJoinMeeting && !eventLogged.current) {
      eventLogged.current = true;
      analytics.logEvent('webinar_joined', {
        objectId: entry.id,
        objectType: entry.type,
        url: entry.meetingLink,
      });
    }
  }, [analytics, canJoinMeeting, entry]);

  return (
    <PageContainer>
      <MediaSection>
        <ExploreContentViewOnlineMeetingMediaContainer entry={entry} />
      </MediaSection>

      <ContentContainer>
        <ContentCard>
          <MainContent>
            <ExploreContentViewOnlineMeetingContent entry={entry} />
          </MainContent>

          <SideContent>
            <ShareButton data={{ title, description, contentType: 'onlineMeeting', contentId: entry.id }} />
            {nextDate && (
              <ReminderCard>
                <ExploreContentViewOnlineMeetingReminder entry={entry} />
              </ReminderCard>
            )}
            <ExploreContentViewHostCard host={entry.hosts[0]} />
          </SideContent>
        </ContentCard>
      </ContentContainer>
    </PageContainer>
  );
});
